import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import api from '../services/api';

const ShieldLogo = () => (
  <div style={{ marginBottom: 28, textAlign: 'center' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
      <svg width="28" height="33" viewBox="-1 -1 34 40" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 1 L31 6 L31 21 Q31 33 16 37 Q1 33 1 21 L1 6 Z" fill="white" stroke="#0A1628" strokeWidth="2"/>
        <path d="M6 13 Q11 9 16 12 Q21 15 26 11" stroke="#0A1628" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
        <path d="M6 18 Q11 14 16 17 Q21 20 26 16" stroke="#0066FF" strokeWidth="1.8" fill="none" strokeLinecap="round"/>
        <path d="M6 23 Q11 19 16 22 Q21 25 26 21" stroke="#0066FF" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity="0.6"/>
        <path d="M7 28 Q11 24 16 27 Q21 30 25 26" stroke="#0066FF" strokeWidth="1.1" fill="none" strokeLinecap="round" opacity="0.3"/>
      </svg>
      <div style={{ fontFamily: 'var(--font-heading)', fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px' }}>
        <span style={{ color: '#0066FF' }}>Air</span><span style={{ color: '#0A1628' }}>Comply</span>
      </div>
    </div>
  </div>
);

const Footer = () => (
  <div style={{
    position: 'fixed', bottom: 20, left: 0, right: 0,
    display: 'flex', justifyContent: 'center', gap: 20,
    fontSize: 12, color: '#9CA3AF',
  }}>
    <a href="https://aircomply.com/terms.html" target="_blank" rel="noopener noreferrer"
      style={{ color: '#9CA3AF', textDecoration: 'none' }}>Terms of Service</a>
    <span>·</span>
    <a href="https://aircomply.com/privacy.html" target="_blank" rel="noopener noreferrer"
      style={{ color: '#9CA3AF', textDecoration: 'none' }}>Privacy Policy</a>
  </div>
);

const pageStyle = {
  minHeight: '100vh', background: 'white',
  display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
  padding: '20px', fontFamily: 'var(--font-body)',
};

const cardStyle = {
  width: '100%', maxWidth: 400, background: 'white',
  border: '1px solid #E2E8F0', borderRadius: 16, padding: '36px 32px',
};

const inputStyle = {
  width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB',
  borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-body)',
  color: '#0A1628', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s', background: 'white',
};

const btnStyle = (loading) => ({
  width: '100%', padding: '11px',
  background: loading ? '#94a3b8' : '#0066FF',
  color: 'white', border: 'none', borderRadius: 8,
  fontSize: 14, fontWeight: 600, fontFamily: 'var(--font-body)',
  cursor: loading ? 'not-allowed' : 'pointer',
  transition: 'background 0.15s', marginBottom: 16,
});

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) return setError('Passwords do not match');
    if (formData.newPassword.length < 8) return setError('Password must be at least 8 characters');
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/reset-password', { token, newPassword: formData.newPassword });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  // Invalid token
  if (!token) return (
    <div style={pageStyle}>
      <ShieldLogo />
      <div style={cardStyle}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>❌</div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700, color: '#0A1628', margin: '0 0 10px' }}>Invalid reset link</h2>
          <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 20, fontFamily: 'var(--font-body)' }}>This link is invalid or has expired.</p>
          <Link to="/forgot-password" style={{ color: '#0066FF', fontWeight: 600, fontSize: 13, textDecoration: 'none' }}>Request a new link →</Link>
        </div>
      </div>
      <Footer />
    </div>
  );

  return (
    <div style={pageStyle}>
      <ShieldLogo />

      <div style={cardStyle}>
        {success ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700, color: '#0A1628', margin: '0 0 8px' }}>Password reset!</h2>
            <p style={{ fontSize: 13, color: '#6B7280', fontFamily: 'var(--font-body)' }}>Redirecting you to sign in...</p>
          </div>
        ) : (
          <>
            <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700, color: '#0A1628', margin: '0 0 24px', textAlign: 'center' }}>
              Set new password
            </h1>

            {error && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#DC2626' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* New password */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>New Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNew ? 'text' : 'password'}
                    required
                    value={formData.newPassword}
                    onChange={e => setFormData({...formData, newPassword: e.target.value})}
                    placeholder="Min 8 characters"
                    style={{ ...inputStyle, paddingRight: 40 }}
                    onFocus={e => e.target.style.borderColor = '#0066FF'}
                    onBlur={e => e.target.style.borderColor = '#D1D5DB'}
                  />
                  <button type="button" onClick={() => setShowNew(!showNew)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 0, display: 'flex', alignItems: 'center' }}>
                    {showNew ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Confirm Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={e => setFormData({...formData, confirmPassword: e.target.value})}
                    placeholder="Repeat new password"
                    style={{ ...inputStyle, paddingRight: 40 }}
                    onFocus={e => e.target.style.borderColor = '#0066FF'}
                    onBlur={e => e.target.style.borderColor = '#D1D5DB'}
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 0, display: 'flex', alignItems: 'center' }}>
                    {showConfirm ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading} style={btnStyle(loading)}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>

            <div style={{ textAlign: 'center' }}>
              <Link to="/login" style={{ fontSize: 13, color: '#6B7280', textDecoration: 'none' }}>← Back to sign in</Link>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default ResetPassword;
